import React, { useMemo, useState } from 'react';
import { Button, CustomCheckbox } from '@hwm/ui-lib';
import { Menu } from '@material-ui/core';
import { ShowStateModel } from '../MainGraph';
import { Trans } from '@lingui/macro';
import {
  BoundaryAndActuationsBaseModel,
  GraphActuationsType,
  GraphBoundariesModel,
  GraphBoundariesType,
} from '@shared/models';
import { isEmpty } from 'lodash';

interface GraphBoundariesProps {
  showState: ShowStateModel;
  setShowState: React.Dispatch<React.SetStateAction<ShowStateModel>>;
  canDisplayActuations: boolean;
  /**
   * `dataTestId` - name of a Graph Boundaries (preferably `<graph_name>-boundaries`)
   * @example
   * ```tsx
   * <GraphBoundaries {...props} dataTestId="test-graph-boundaries" />
   */
  dataTestId: string;
}

export const GraphBoundaries: React.FC<GraphBoundariesProps> = ({
  showState,
  setShowState,
  canDisplayActuations = false,
  dataTestId,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const canDisplayBoundaries = useMemo(() => Boolean(showState.bands) && !isEmpty(showState.bands), [showState.bands]);

  return (
    <div className="flex" data-testid={`${dataTestId}-wrapper`}>
      {(canDisplayBoundaries || canDisplayActuations) && (
        <Button
          variant="text"
          style={{ height: 28, padding: 4, marginRight: 12, width: 188 }}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
          dataTestId={`${dataTestId}-button`}
        >
          <Trans>Boundaries and Actuations</Trans>
        </Button>
      )}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        getContentAnchorEl={null}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        classes={{ paper: 'px-4 py-2' }}
      >
        <div className="flex flex-col" data-testid={`${dataTestId}`}>
          {canDisplayBoundaries &&
            Object.entries(showState.bands)?.map(([key, { label, state }]: [string, BoundaryAndActuationsBaseModel]) =>
              key === GraphBoundariesType.Actuator && !canDisplayActuations ? null : (
                <CustomCheckbox
                  key={key}
                  label={label}
                  checked={state}
                  onChange={(_, check) => {
                    const newState = { ...showState.bands };

                    // set to false and then to proper state
                    // updates ui in case there is a boundary with tooltip only
                    setShowState((p) => ({
                      ...p,
                      bands: Object.fromEntries(
                        Object.entries(p.bands ?? {}).map(([key, band]) => [
                          key,
                          {
                            ...band,
                            state: false,
                          },
                        ])
                      ) as GraphBoundariesModel<BoundaryAndActuationsBaseModel>,
                    }));

                    setTimeout(() => {
                      newState[key as GraphBoundariesType].state = check;
                      setShowState((p) => ({
                        ...p,
                        bands: newState,
                      }));
                    }, 0);
                  }}
                  wrapperClasses="pr-2"
                  dataTestId={`${dataTestId}-band-${key.toLowerCase()}`}
                />
              )
            )}
          {canDisplayActuations && (
            <>
              {canDisplayBoundaries && <div className={`border-b-2 border-lightGrey my-2`} />}
              {Object.entries(showState.actuations)?.map(([key, { label, state }]) => (
                <CustomCheckbox
                  key={key}
                  label={label}
                  checked={state}
                  onChange={(_, check) => {
                    const newState = { ...showState.actuations };

                    newState[key as GraphActuationsType].state = check;
                    setShowState((p) => ({
                      ...p,
                      actuations: newState,
                    }));
                  }}
                  wrapperClasses="pr-2"
                  dataTestId={`${dataTestId}-actuation-${key.toLowerCase()}`}
                />
              ))}
            </>
          )}
        </div>
      </Menu>
    </div>
  );
};
